import React from 'react';
import { motion } from 'framer-motion';
import { Shield, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const PrivacyPolicy = () => {
  return (
    <div
      className="min-h-screen w-full"
      style={{
        background: 'linear-gradient(140deg, #08101e 0%, #0f172a 55%, #0b3b4b 100%)'
      }}
    >
      {/* Header */}
      <motion.header
        className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{
          background: 'rgba(11, 18, 32, 0.7)',
          borderColor: 'rgba(var(--line), 0.6)'
        }}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo />
            <Link
              to="/login"
              className="px-4 py-2 rounded-lg transition-all"
              style={{ background: 'var(--accent-cyan)', color: '#0b1220' }}
            >
              Retour
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <Shield size={64} className="mx-auto mb-4" style={{ color: 'var(--accent-cyan)' }} />
          <h1
            className="text-4xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--accent-cyan) 0%, var(--accent-ocean) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Politique de Confidentialité
          </h1>
          <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
            Application Coach Life - OpenMeta
          </p>
        </motion.div>

        {/* Content Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="backdrop-blur-xl rounded-2xl p-8 border space-y-8"
          style={{
            background: 'rgba(var(--bg-elev-1), 0.8)',
            borderColor: 'rgba(var(--line), 0.6)',
            color: 'var(--text-primary)'
          }}
        >
          {/* Important Notice */}
          <div
            className="p-4 rounded-lg border-l-4"
            style={{
              background: 'rgba(6, 182, 212, 0.1)',
              borderColor: 'var(--accent-cyan)'
            }}
          >
            <h3 className="font-bold mb-2" style={{ color: 'var(--accent-cyan)' }}>🔒 Engagement de Confidentialité</h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              OpenMeta s'engage à protéger vos données personnelles et respecter votre vie privée conformément au Règlement Général sur la Protection des Données (RGPD).
            </p>
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              1. Responsable du Traitement
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Le responsable du traitement des données à caractère personnel est :
            </p>
            <ul className="space-y-1" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />OpenMeta, pour la recherche et l'innovation</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Centre de l'innovation technologique – 61330 Céaucé, FRANCE</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Email : support@openmeta.fr</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Directeur de la publication : Dr Yanis Belhatri</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              2. Données Collectées
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Dans le cadre de l'utilisation de l'Application Coach Life, nous collectons les données suivantes :
            </p>
            
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              2.1. Données de Compte
            </h3>
            <ul className="space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Adresse email</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Nom d'utilisateur</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Mot de passe (crypté)</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Statut d'abonnement</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              2.2. Données d'Utilisation
            </h3>
            <ul className="space-y-1 mb-4" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Historique des conversations avec l'IA</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Fichiers et images téléchargés</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Préférences linguistiques</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Données d'interaction vocale (enregistrements temporaires)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              2.3. Données Techniques
            </h3>
            <ul className="space-y-1" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Adresse IP</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Type de navigateur et version</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Système d'exploitation</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Données de connexion (dates et heures d'accès)</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              3. Finalités du Traitement
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Vos données personnelles sont collectées et traitées pour les finalités suivantes :
            </p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Gestion du compte utilisateur</strong> : Création, authentification et gestion de votre compte</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Fourniture des services</strong> : Accès aux fonctionnalités de l'IA conversationnelle et vocale</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Amélioration des services</strong> : Analyse des interactions pour améliorer les performances de l'IA</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Gestion des abonnements</strong> : Traitement des paiements et suivi du statut d'abonnement</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Support client</strong> : Réponse à vos demandes et résolution de problèmes techniques</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Sécurité</strong> : Prévention des fraudes et protection contre les abus</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              4. Base Légale du Traitement
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Le traitement de vos données repose sur les bases légales suivantes :
            </p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Exécution du contrat</strong> : Nécessaire pour fournir les services de l'Application</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Consentement</strong> : Pour certaines données sensibles (conversations, fichiers uploadés)</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Intérêt légitime</strong> : Pour l'amélioration de nos services et la sécurité</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Obligation légale</strong> : Conservation de certaines données pour respecter nos obligations légales</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              5. Destinataires des Données
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Vos données peuvent être transmises aux destinataires suivants :
            </p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Personnel d'OpenMeta</strong> : Équipes techniques et support client</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Prestataires de services</strong> : Hébergement (OVH), Services IA (OpenAI), Paiement (Stripe)</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Autorités</strong> : En cas d'obligation légale ou de demande judiciaire</li>
            </ul>
            <div className="mt-4 p-3 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                <strong className="text-red-400">⚠️ Important :</strong> Vos conversations sont traitées par OpenAI (fournisseur de l'IA GPT-5.2). OpenAI s'engage à ne pas utiliser vos données pour entraîner ses modèles lorsqu'elles sont transmises via l'API.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              6. Durée de Conservation
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Les données sont conservées pendant les durées suivantes :
            </p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Données de compte</strong> : Pendant toute la durée d'utilisation du service + 3 ans après la fermeture du compte</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Historique des conversations</strong> : Conservé tant que le compte est actif, supprimable à tout moment par l'utilisateur</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Données vocales</strong> : Stockées temporairement pendant la session, supprimées automatiquement après déconnexion</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Données de paiement</strong> : Conservées conformément aux obligations légales (10 ans)</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Logs techniques</strong> : 12 mois maximum</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              7. Vos Droits
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Droit d'accès</strong> : Obtenir la confirmation que vos données sont traitées et en obtenir une copie</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Droit de rectification</strong> : Corriger ou compléter vos données inexactes ou incomplètes</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Droit à l'effacement</strong> : Demander la suppression de vos données dans certaines conditions</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Droit à la limitation</strong> : Limiter le traitement de vos données dans certains cas</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Droit à la portabilité</strong> : Recevoir vos données dans un format structuré et lisible par machine</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Droit d'opposition</strong> : Vous opposer au traitement de vos données pour des raisons légitimes</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Droit de retirer votre consentement</strong> : Pour les traitements basés sur le consentement</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Droit de définir des directives post-mortem</strong> : Concernant la conservation et la communication de vos données</li>
            </ul>
            <div className="mt-4 p-4 rounded-lg" style={{ background: 'rgba(6, 182, 212, 0.1)' }}>
              <p style={{ color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--accent-cyan)' }}>Pour exercer vos droits :</strong><br />
                Envoyez un email à <a href="mailto:support@openmeta.fr" style={{ color: 'var(--accent-cyan)' }}>support@openmeta.fr</a> avec pour objet "Exercice de mes droits RGPD" en précisant votre demande et en joignant une copie de votre pièce d'identité.
              </p>
              <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                Nous nous engageons à répondre dans un délai d'un mois maximum.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              8. Sécurité des Données
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              OpenMeta met en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre :
            </p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />La destruction accidentelle ou illicite</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />La perte, l'altération ou la divulgation non autorisée</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />L'accès non autorisé</li>
            </ul>
            <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
              Ces mesures incluent notamment : chiffrement des données sensibles (mots de passe), connexions sécurisées HTTPS, contrôles d'accès stricts, sauvegardes régulières, et hébergement sécurisé chez OVH (France).
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              9. Transferts Internationaux
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Vos données peuvent être transférées vers des pays hors de l'Union Européenne, notamment vers les États-Unis (OpenAI pour le traitement des conversations IA).
            </p>
            <p style={{ color: 'var(--text-secondary)' }}>
              Ces transferts sont encadrés par des garanties appropriées conformément au RGPD, notamment les Clauses Contractuelles Types de la Commission Européenne. OpenAI est certifié sous le cadre de protection des données UE-États-Unis (Data Privacy Framework).
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              10. Cookies et Technologies Similaires
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              L'Application utilise des technologies de stockage local pour :
            </p>
            <ul className="space-y-2" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Authentification</strong> : Stockage du token JWT pour maintenir votre session</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} /><strong>Préférences</strong> : Sauvegarde de vos choix (langue, thème)</li>
            </ul>
            <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
              Ces données sont stockées localement dans votre navigateur et peuvent être supprimées à tout moment via les paramètres de votre navigateur.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              11. Modifications de la Politique
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              OpenMeta se réserve le droit de modifier la présente Politique de Confidentialité à tout moment. Toute modification sera publiée sur cette page avec une nouvelle date de mise à jour. En cas de modification substantielle, nous vous en informerons par email.
            </p>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-muted)' }}>
              <strong>Dernière mise à jour :</strong> Janvier 2026
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--accent-cyan)' }}>
              12. Réclamation auprès de la CNIL
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Si vous estimez que vos droits ne sont pas respectés, vous avez le droit d'introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
            </p>
            <ul className="space-y-1" style={{ color: 'var(--text-secondary)' }}>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />CNIL - 3 Place de Fontenoy, TSA 80715, 75334 PARIS CEDEX 07</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Téléphone : 01 53 73 22 22</li>
              <li><ChevronRight size={16} className="inline mr-2" style={{ color: 'var(--accent-ocean)' }} />Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-cyan)' }}>www.cnil.fr</a></li>
            </ul>
          </section>

          {/* Contact */}
          <section
            className="p-4 rounded-lg"
            style={{ background: 'rgba(6, 182, 212, 0.1)' }}
          >
            <h3 className="font-bold mb-2" style={{ color: 'var(--accent-cyan)' }}>📧 Contact</h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              Pour toute question concernant la présente Politique de Confidentialité ou vos données personnelles :
            </p>
            <p className="mt-2">
              <a href="mailto:support@openmeta.fr" className="font-semibold" style={{ color: 'var(--accent-cyan)' }}>
                support@openmeta.fr
              </a>
            </p>
          </section>
        </motion.div>

        {/* Terms Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-6 text-center"
        >
          <Link
            to="/terms"
            className="text-sm hover:underline"
            style={{ color: 'var(--accent-ocean)' }}
          >
            Conditions générales d'utilisation →
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
